import React, { useEffect } from 'react';

interface ClickAnalyzerProps {
  targetRef: React.RefObject<HTMLDivElement>;
  onCustomClickEvent: (eventType: 'single' | 'double' | 'long' | 'context') => void;
}

const ClickAnalyzer: React.FC<ClickAnalyzerProps> = ({ targetRef, onCustomClickEvent }) => {
  useEffect(() => {
    const target = targetRef.current;
    let lastClickTime = 0;
    const clickDelay = 300; // milliseconds to distinguish between single and double clicks
    const longPressThreshold = 500; // milliseconds to trigger a long press
    let clickTimeout: ReturnType<typeof setTimeout> | null = null;
    let longPressTimer: ReturnType<typeof setTimeout> | null = null;

    if (!target) return;

    const clearTimers = () => {
      if (clickTimeout) {
        clearTimeout(clickTimeout);
        clickTimeout = null;
      }
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }
    };

    const handleMouseDown = (event: MouseEvent | TouchEvent) => {
      event.preventDefault();
      clearTimers();

      longPressTimer = setTimeout(() => {
        onCustomClickEvent('long');
        longPressTimer = null; // Prevent firing again
        // Once a long press is detected, clear the click timeout to prevent single or double click detection
        clearTimers();
      }, longPressThreshold);
    };

    const handleMouseUp = () => {
      // If long press timer hasn't triggered, proceed to check for single or double click
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = null;

        const now = Date.now();
        if (now - lastClickTime < clickDelay) {
          // Double click detected
          onCustomClickEvent('double');
        } else {
          // Setup for single click detection
          clickTimeout = setTimeout(() => {
            onCustomClickEvent('single');
          }, clickDelay);
        }
        lastClickTime = now;
      }
    };

    const handleContextMenu = (event: MouseEvent) => {
      event.preventDefault();
      clearTimers(); // Prevent triggering other events after context menu
      onCustomClickEvent('context');
    };

    target.addEventListener('mousedown', handleMouseDown);
    target.addEventListener('mouseup', handleMouseUp);
    target.addEventListener('contextmenu', handleContextMenu);

    // Ensure compatibility with touch devices
    target.addEventListener('touchstart', handleMouseDown);
    target.addEventListener('touchend', handleMouseUp);

    return () => {
      target.removeEventListener('mousedown', handleMouseDown);
      target.removeEventListener('mouseup', handleMouseUp);
      target.removeEventListener('contextmenu', handleContextMenu);
      target.removeEventListener('touchstart', handleMouseDown);
      target.removeEventListener('touchend', handleMouseUp);
    };
  }, [targetRef, onCustomClickEvent]);

  return null;
};

export default ClickAnalyzer;
